import { vec3 } from 'gl-matrix';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { getRandomInt } from './number';

/**
 * The minimum intensity value used to determine if a color is considered "grey".
 * Colors with all channels within this threshold will be considered "grey".
 */
const GREY_THRESHOLD = 16;

/**
 * Darkening factor for copied ROI colors.
 * 0.85 means the color will be 85% of the original brightness (15% darker).
 */
const COPY_DARKEN_FACTOR = 0.85;

/**
 * Minimum channel value to prevent colors from becoming too dark after multiple copies.
 */
const MIN_CHANNEL_VALUE = 30;

/**
 * Generates a random RGB color (0-255) with nudging to avoid too many similar grey colors
 * and avoids generating similar colors in succession.
 * Returns an array: [r, g, b]
 */

const MIN_COLOR_DISTANCE = 100; // Minimum Euclidean distance between colors
const MIN_BRIGHTNESS = 80; // Minimum sum of RGB channels to avoid dark colors
const PREVIOUS_COLORS_LIMIT = 10;
const previousColors: [number, number, number][] = [];

export const generateRandomRgbColor = (): [number, number, number] => {
    let attempts = 0;
    let r: number, g: number, b: number;
    do {
        r = getRandomInt(0, 255);
        g = getRandomInt(0, 255);
        b = getRandomInt(0, 255);

        // Check if color is "too grey" (channels close together)
        const maxDiff = Math.max(
            Math.abs(r - g),
            Math.abs(g - b),
            Math.abs(b - r)
        );
        if (maxDiff < GREY_THRESHOLD) {
            // Pick a channel to nudge
            const channel = getRandomInt(0, 2);
            switch (channel) {
                case 0:
                    r = getRandomInt(0, 255);
                    break;
                case 1:
                    g = getRandomInt(0, 255);
                    break;
                case 2:
                    b = getRandomInt(0, 255);
                    break;
            }
        }

        // Avoid colors that are too dark
        const brightness = r + g + b;
        if (brightness < MIN_BRIGHTNESS) {
            attempts++;
            continue;
        }

        // Check against previous 10 colors
        let tooClose = false;
        for (const prev of previousColors) {
            const dist = Math.sqrt(
                Math.pow(r - prev[0], 2) +
                    Math.pow(g - prev[1], 2) +
                    Math.pow(b - prev[2], 2)
            );
            if (dist < MIN_COLOR_DISTANCE) {
                tooClose = true;
                break;
            }
        }
        if (tooClose) {
            attempts++;
            continue;
        }
        break;
    } while (attempts < 20); // Avoid infinite loop

    // Track previous colors
    previousColors.push([r, g, b]);
    if (previousColors.length > PREVIOUS_COLORS_LIMIT) {
        previousColors.shift();
    }
    return [r, g, b];
};

/**
 * Generates a random DICOM color object with nudging to avoid too many similar grey colors.
 */
export const generateRandomDicomColor = (): DicomColor => {
    const [R, G, B] = generateRandomRgbColor();
    return { R, G, B };
};

/**
 * CSS colour value for a DICOM 8-bit colour, for a style prop or a canvas
 * fill. {@link formatColorRgb} is the same value as a display label.
 */
export const dicomColorToRgbString = (color: DicomColor) =>
    `rgb(${color.R}, ${color.G}, ${color.B})`;

/**
 * A DICOM 8-bit colour as a label to show the user, e.g. in a tooltip.
 * Differs from the CSS value only in case, which CSS ignores.
 */
export const formatColorRgb = (color: DicomColor) =>
    dicomColorToRgbString(color).toUpperCase();

/**
 * A DICOM colour (0..255 per channel) as a normalised RGB vector (0..1
 * per channel) — the form the engine holds colours in and every shader
 * and preview takes them in.
 */
export const dicomColorToVec3 = (color: DicomColor): vec3 =>
    vec3.fromValues(color.R / 255, color.G / 255, color.B / 255);

/**
 * Generates a slightly darker version of the provided color.
 * Useful for distinguishing copied ROIs from their source.
 *
 * @param color - The original DicomColor to darken
 * @param factor - Optional darkening factor (0-1), defaults to COPY_DARKEN_FACTOR
 * @returns A new DicomColor that is slightly darker than the original
 */
export const generateDarkerColor = (
    color: DicomColor,
    factor: number = COPY_DARKEN_FACTOR
): DicomColor => {
    return {
        R: Math.max(MIN_CHANNEL_VALUE, Math.round(color.R * factor)),
        G: Math.max(MIN_CHANNEL_VALUE, Math.round(color.G * factor)),
        B: Math.max(MIN_CHANNEL_VALUE, Math.round(color.B * factor)),
    };
};
